/** Single source of truth for option values; used by constants (options) and schema (z.enum). */

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I
) => void
  ? I
  : never

type LastInUnion<U> = UnionToIntersection<
  U extends any ? (x: U) => 0 : never
> extends (x: infer L) => 0
  ? L
  : never

type UnionToTuple<U, Last = LastInUnion<U>> = [U] extends [never]
  ? []
  : [...UnionToTuple<Exclude<U, Last>>, Last]

export const Titles = {
  '1': 'sr',
  '2': 'sra',
} as const

/** Tuple is source of truth so we get literal type ["1", "2"]; Titles must match. */
export const TitleValues = Object.keys(Titles) as UnionToTuple<
  keyof typeof Titles
>

export type CofidisTitleCode = keyof typeof Titles

export const EditionMode = {
  D: 'digitalSign',
  P: 'pdfFormat',
} as const

export const EditionModeValues = Object.keys(EditionMode) as UnionToTuple<
  keyof typeof EditionMode
>
export type CofidisEditionMode = keyof typeof EditionMode

/** How regulatory/legal documents reach the customer; E goes to the form email. */
export const SustainableSupport = {
  E: 'electronic',
  P: 'paper',
} as const

export const SustainableSupportValues = Object.keys(
  SustainableSupport
) as UnionToTuple<keyof typeof SustainableSupport>
export type CofidisSustainableSupport = keyof typeof SustainableSupport

export const FamilySituation = {
  C: 'single',
  D: 'divorced',
  M: 'married',
  S: 'separated',
  K: 'commonLaw',
  V: 'widowed',
} as const

export const FamilySituationValues = Object.keys(
  FamilySituation
) as UnionToTuple<keyof typeof FamilySituation>

export type CofidisFamilySituation = keyof typeof FamilySituation
/** Slug for i18n key lookup (e.g. familySituation.options.{slug}.label) */
export type FamilySituationSlug =
  (typeof FamilySituation)[CofidisFamilySituation]

export const HomeType = {
  A: 'ownerMortgage',
  C: 'caravan',
  E: 'companyHousing',
  H: 'hotel',
  L: 'tenant',
  P: 'ownerNoMortgage',
  V: 'parents',
  X: 'other',
} as const

export const HomeTypeValues = Object.keys(HomeType) as UnionToTuple<
  keyof typeof HomeType
>
export type CofidisHomeType = keyof typeof HomeType
/** Slug for i18n key lookup (e.g. homeType.options.{slug}.label) */
export type HomeTypeSlug = (typeof HomeType)[CofidisHomeType]

export const HomeExpensesConfig: Partial<
  Record<CofidisHomeType, { required: boolean; label: string }>
> = {
  A: {
    required: true,
    label: HomeType.A,
  },
  L: {
    required: true,
    label: HomeType.L,
  },
}

export const ContractTypeValues = [
  'CDD',
  'CDI',
  'EVE',
  'FDI',
  'FOR',
  'INT',
  'STG',
] as const
export type CofidisContractType = (typeof ContractTypeValues)[number]

/** Contract types that require contractEndDate (temporary contracts). */
export const TemporaryContractTypes: CofidisContractType[] = [
  'CDD',
  'EVE',
  'FDI',
  'FOR',
  'INT',
  'STG',
]

export const SalaryMonthsValues = ['12', '13', '14'] as const
export type CofidisSalaryMonthsNumber = (typeof SalaryMonthsValues)[number]

export const IdTypeValues = ['NIF', 'NIE'] as const
export type CofidisIdType = (typeof IdTypeValues)[number]

export const WayTypeValues = [
  'AD',
  'AL',
  'AO',
  'AP',
  'AV',
  'BA',
  'BL',
  'BO',
  'BU',
  'CH',
  'CL',
  'CM',
  'CO',
  'CR',
  'CS',
  'CT',
  'ED',
  'GL',
  'GR',
  'LG',
  'MC',
  'MN',
  'MZ',
  'PB',
  'PG',
  'PJ',
  'PQ',
  'PR',
  'PS',
  'PZ',
  'RB',
  'RD',
  'TR',
  'UR',
] as const
export type CofidisWayType = (typeof WayTypeValues)[number]

export type CofidisOptInFlag = 0 | 1
